import styled from 'styled-components';
import breakpoint from '~/utils/style-utils';

export const GameCardContainer = styled.div<{ radius: string; disableHover: boolean; showBlurredOverlay?: boolean }>`
	--borderRadius: ${({ radius }) => radius || '12px'};
	--blurSize: 30px;

	position: relative;
	border-radius: var(--borderRadius);
	overflow: visible;
	width: 100%;
	height: 100%;
	cursor: ${({ disableHover }) => (disableHover ? 'pointer' : 'unset')};

	img {
		border-radius: var(--borderRadius);
	}

	${({ disableHover }) =>
		!disableHover
			? breakpoint.lg`
		&:hover {
			#card-bottom-container,
			[data-testid='active-players'] {
				filter: blur(var(--blurSize));
			}

			&::after,
			picture::after {
				content: '';
				display: inline-block;
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				overflow: hidden;
				backdrop-filter: blur(var(--blurSize));
				border-radius: var(--borderRadius);
				background: rgba(0, 0, 0, 0.3);
			}

			@supports (-moz-appearance: none) {
				--firefoxBlurSize: 2px;
				img {
					filter: blur(var(--firefoxBlurSize));
					-moz-filter: blur(var(--firefoxBlurSize));
				}
			}

			#card-hover {
				display: flex;
			}
		}
	`
			: ''};

	${({ showBlurredOverlay }) =>
		showBlurredOverlay
			? `
		#card-bottom-container,
			[data-testid='active-players'] {
				display: none;
			}

			&::after,
			picture::after {
				content: '';
				display: inline-block;
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				overflow: hidden;
				backdrop-filter: blur(var(--blurSize));
				border-radius: var(--borderRadius);
				background: rgba(0, 0, 0, 0.3);
			}

			@supports (-moz-appearance: none) {
				--firefoxBlurSize: 2px;
				img {
					filter: blur(var(--firefoxBlurSize));
					-moz-filter: blur(var(--firefoxBlurSize));
				}
			}
	`
			: ''};
`;

const CARD_INTERFACE_ATTR = ({ bodyScrollLockIgnore }: { bodyScrollLockIgnore: boolean }) => ({
	'data-body-scroll-lock-ignore': bodyScrollLockIgnore
});

export const CardInterface = styled.div.attrs<{ bodyScrollLockIgnore: boolean }, typeof CARD_INTERFACE_ATTR>(
	CARD_INTERFACE_ATTR
)`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
`;

export const CardHeaderContainer = styled.div`
	position: absolute;
	z-index: 1;
	top: 0;
	left: 0;
	width: 100%;
	display: flex;
	flex-wrap: nowrap;
	justify-content: flex-start;
`;

export const CardBottomContainer = styled.div`
	position: absolute;
	left: 0;
	bottom: 0;
	width: 100%;
`;

export const GameHover = styled.div`
	display: none;
	pointer-events: none;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	border-radius: 12px;
	position: absolute;
	top: 0;
	left: 0;
	z-index: 1;
	width: 100%;
	height: 100%;

	overflow: hidden;
`;

export const GameHoverTop = styled.div`
	width: 100%;
	pointer-events: none;

	overflow-wrap: anywhere;

	animation-name: appearTop;
	animation-duration: 0.15s;
	animation-timing-function: ease-out;

	margin-bottom: 18px;

	position: relative;

	@keyframes appearTop {
		from {
			opacity: 1;
			top: -50%;
		}
		to {
			opacity: 0;
			top: 0;
		}
	}
`;

export const GameName = styled.div`
	font-size: 17px;
	line-height: 24px;
	text-align: center;
	color: rgba(var(--active-players-color), 1);
	font-weight: 600;
	padding: 0 2px;
`;

export const ProviderName = styled.div`
	font-size: 12px;
	line-height: 16px;
	text-align: center;
	color: rgba(var(--active-players-color), 1);
	font-weight: 400;
`;

export const GameHoverBottom = styled.div`
	width: 100%;
	pointer-events: auto;

	animation-name: appearBottom;
	animation-duration: 0.15s;
	animation-timing-function: ease-out;

	position: relative;

	@keyframes appearBottom {
		0% {
			transform: scale(0.01);
			bottom: -50%;
		}
		99% {
			transform: scale(1.1);
		}
		100% {
			transform: scale(1);
			bottom: 0;
		}
	}
`;

export const PlayButton = styled.button`
	cursor: pointer;
	display: block;
	background-color: var(--button-primary-bg);
	color: var(--button-primary-text);
	border-radius: 50px;
	padding: 8px 24px;
	margin: 0 auto;
	margin-bottom: 12px;
	font-size: 15px;
	line-height: 20px;
	font-weight: 600;
	position: relative;

	&:hover {
		&::after {
			content: '';
			position: absolute;
			z-index: 1;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background-color: white;
			opacity: 0.1;
			border-radius: 50px;
		}
	}
`;

export const PracticeButtonContainer = styled.div`
	width: 100%;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
`;

export const PracticeButton = styled.button`
	cursor: pointer;
	font-size: 13px;
	font-weight: 400;
	line-height: 18px;
	color: white;

	&:hover {
		color: var(--button-primary-bg);
	}
`;

export const GameClosedInfo = styled.div`
	color: rgba(var(--font-light));
	font: var(--h4-bold);
	display: flex;
	flex-flow: column nowrap;
	justify-content: center;
	align-items: center;
	height: 100%;
	position: relative;
	z-index: 1;
`;
